---
title: "What Is a CFD? Trading Price Movement Without Owning the Asset"
slug: what-is-a-cfd
shortTitle: "What Is a CFD?"
description: "A plain-English guide to contracts for difference: how a CFD tracks a price, how profit and loss are worked out in USDT, and what leverage and costs come with it."
level: beginner
category: markets
course: beginner
order: 4
topics: [cfds, leverage-margin]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/what-is-a-cfd.svg
faqs:
  - q: "Do I own Bitcoin or gold when I trade a CFD on it?"
    a: "No. A CFD is a contract with the platform that pays out the change in price. You never hold the coin, the metal or the share, so there is no wallet, vault or share certificate involved, and you have no voting rights or delivery."
  - q: "Can I lose more than I put into a CFD trade?"
    a: "Your loss on a position is the price move times your position size, and with leverage that can exceed the margin you set aside for it. The platform closes positions at the stop-out level to limit this, but fast markets and gaps can still cause losses larger than expected. Always size trades so that a loss you can accept is reached well before stop-out."
  - q: "Why are CFD prices slightly different from the price on a crypto exchange?"
    a: "A CFD price follows a reference price for the underlying market, then adds a spread between the buy (ask) and sell (bid) prices. The spread, data sources and timing mean the CFD quote can differ a little from any single exchange at any given moment."
  - q: "Is a CFD the same as a futures contract?"
    a: "They are related but not the same. Futures are standardised contracts traded on an exchange with fixed expiry dates. The CFDs on this platform have no expiry date; you hold them for as long as you choose, and pay a daily holding cost (swap) on some markets instead."
  - q: "Are CFDs suitable for beginners?"
    a: "They can be learned, but they are leveraged and high risk, so it is sensible to start with small positions, always use a stop loss, and practise on a demo account first if one is available to you. Only trade money you can afford to lose."
---

When you buy a coin on an exchange, you own it: it sits in a wallet and you can send it somewhere else. A **contract for difference (CFD)** works differently. You and the platform agree to settle the *difference* in price between when you open a trade and when you close it. If the price moves your way, the difference is paid to you; if it moves against you, the difference is taken from your balance.

That simple idea is why one {{BRAND}} account can trade Bitcoin, gold, oil, shares and indices side by side, all settled in USDT. This lesson explains what a CFD is, how profit and loss are calculated, what it costs to hold one, and why leverage deserves your full attention.

<Warning title="CFDs are leveraged products">

CFDs let you control a large position with a smaller deposit. That magnifies losses as much as gains, and losses can happen quickly. Many traders lose money trading CFDs. Make sure you understand how they work before you place a trade, and never risk money you cannot afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **You trade the price, not the asset.** A CFD pays out the change in price between opening and closing; you never own the coin, metal or share.
- **Profit and loss are simple to work out.** Price change × position size, settled in USDT, minus costs such as the spread, the trade fee and any swap.
- **You can go long or short.** Buy if you expect the price to rise, sell if you expect it to fall; the math is the same in reverse.
- **Leverage cuts both ways.** It reduces the margin you need, but every loss is calculated on the full position size, not on your margin.

</KeyTakeaways>

## What a CFD actually is

A CFD is a contract between you and the platform that mirrors the price of an underlying market. The underlying can be almost anything with a reliable price: a cryptocurrency, a commodity such as gold, a company's shares or a stock index.

Three things make a CFD different from buying the asset outright:

- **No ownership.** You hold a position, not the asset. There is nothing to store, insure or take delivery of.
- **Both directions.** Because you never need to own the asset, you can open a *sell* position just as easily as a *buy* position.
- **Margin instead of full payment.** You put up a fraction of the position's value as margin. The rest is effectively exposure you are carrying on top of your deposit.

## How a CFD trade works, step by step

<Steps>
<Step title="Pick a market">

Choose what you want to trade, for example BTC/USDT or GOLD/USDT. Each market shows two prices: the **Ask** (what you pay to buy) and the **Bid** (what you receive when you sell).

</Step>
<Step title="Choose a direction and size">

Press **Buy** if you think the price will rise or **Sell** if you think it will fall, then enter a size. The order panel shows how much margin the trade needs and how much one dollar of price movement is worth to you.

</Step>
<Step title="Protect the trade">

Set a stop loss (where you will accept being wrong) and, if you like, a take profit (where you will bank a gain). Both close the position automatically.

</Step>
<Step title="Close and settle">

When the position closes, the price difference times your size is added to or taken from your USDT balance, along with any costs.

</Step>
</Steps>

## Working out profit and loss in USDT

The formula is the same for every CFD:

**Profit or loss = (closing price − opening price) × position size**, reversed for a sell.

<Example title="A Bitcoin CFD (illustrative prices)">

You buy a position worth 0.05 BTC at 60,000 USDT. Its value is 0.05 × 60,000 = **3,000 USDT**.

- If you close at 61,500, the result is (61,500 − 60,000) × 0.05 = **+75 USDT** before costs.
- If you close at 58,800, the result is (58,800 − 60,000) × 0.05 = **−60 USDT** before costs.

</Example>

A sell position works in mirror image. If you had *sold* the same 0.05 BTC at 60,000 and closed at 58,800, you would make 60 USDT; if the price rose to 61,500, you would lose 75 USDT.

<CompareTable caption="The same 0.05 BTC position, long and short">

| Closing price | Buy (long) result | Sell (short) result |
| --- | --- | --- |
| 61,500 USDT | +75 USDT | −75 USDT |
| 60,000 USDT | 0 USDT | 0 USDT |
| 58,800 USDT | −60 USDT | +60 USDT |

</CompareTable>

The figures above ignore costs. In practice the spread means a new position starts slightly negative, and the trade fee and any swap are added on top.

## CFD versus buying the asset

<CompareTable caption="Buying the asset compared with trading a CFD on it">

| | Buying the asset | Trading a CFD |
| --- | --- | --- |
| Ownership | You own it and can withdraw it | You hold a contract on its price |
| Direction | Profit only if the price rises | Buy or sell, so either direction |
| Money needed | The full price | Margin, a fraction of the position's value |
| Holding cost | Storage or custody, if any | Swap on some markets, charged daily |
| Main risk | The asset falls in value | Leverage magnifies losses beyond your margin |

</CompareTable>

Neither is better in general. Owning an asset suits a long-term holder who wants the thing itself. A CFD suits a trader who wants to act on price movements in either direction, across many markets, from one USDT balance.

## What a CFD costs

Every trade has costs, and they are easy to overlook when you focus on the price:

1. **Spread.** The gap between Bid and Ask. You buy at the higher price and sell at the lower one, so every position opens slightly in the red.
2. **Trade fee.** {{BRAND}} shows {{TRADE_FEE}}; check the order summary line labelled **Fee**.
3. **Swap.** A daily holding cost (or occasionally a credit) on positions kept open past {{SWAP_HOUR}}. On non-crypto markets it is charged three times on {{SWAP_TRIPLE_DAY}} to cover the weekend.

{/* TODO: verify with platform — current fee schedule, crypto swap rates and which markets carry a swap. */}

<ProTip>

Before you confirm any order, read the summary under the order button. **Margin required**, **Fee** and **Value of $1 move** tell you, in USDT, what the trade ties up, what it costs to open, and how much each dollar of price movement is worth to you.

</ProTip>

## Leverage: the part that needs the most respect

Leverage is what lets a 3,000 USDT position be opened with far less than 3,000 USDT. At 10x leverage, that position needs 300 USDT of margin. The catch is that profit and loss are still calculated on the full 3,000 USDT.

A 2% move against you on a 3,000 USDT position is a 60 USDT loss. Measured against 300 USDT of margin, that is 20% of the money set aside for the trade, from a move that happens often in crypto. That is why leverage gets its own lessons later in this path: [Leverage and Margin Explained Simply](/academy/article/leverage-and-margin) and [Liquidation and Margin Calls](/academy/article/liquidation-and-margin-calls).

<Warning>

On {{BRAND}}, margin is shared across your account. If your losses push the account's margin level down to {{STOP_OUT_LEVEL}}, positions are closed automatically, starting with the one losing the most. Size every trade so your stop loss is reached long before that point.

</Warning>

## How CFDs look on {{BRAND}}

Every market on {{BRAND}} is a CFD settled in USDT. Crypto and commodities are shown against USDT, for example BTC/USDT or GOLD/USDT. When you open the trade screen, the order panel on the right holds everything from this lesson: **Buy** and **Sell**, the size in USDT or lots, the **Leverage** button, the **TP / SL** switch and the cost summary.

<Screenshot name="order-panel-overview" alt="The order panel with Buy and Sell, size, leverage and the order summary" caption="The order panel: direction, size, leverage, TP / SL and the cost summary in one place." />

{/* TODO: verify with platform — add a real screenshot of the order panel as order-panel-overview.png. */}

The leverage you can choose is capped per market, up to {{MAX_LEVERAGE}}. You do not have to use the maximum. Because margin is shared across the account, what decides how close you are to stop-out is the total size of your positions compared with your equity; a high leverage setting matters mostly because it lets the same deposit open much bigger positions. Choose the size from the loss you can accept, not from the most the leverage allows.

## Conclusion

A CFD is a contract on price. You never own the underlying asset, you can trade rising and falling markets the same way, and your result is the price difference times your size, minus costs, settled in USDT. That flexibility is useful, but leverage means losses are calculated on the whole position, not on the margin you put down. Learn the mechanics, use a stop loss on every trade, start small, and the next lessons on going long and short, spreads and order types will slot into place.
